import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';

import { getEditProjPerm } from '~/utils';

import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import logoImg from '~/assets/images/logo.png';
import leafRightImg from '~/assets/images/leaf-right.png';
import leafLeftImg from '~/assets/images/leaf-left.png';

import { PROJECT_PATHS } from '~/constants';

import {
  Container,
  Content,
  LeafRightContent,
  LogoContent,
  InfoContent,
  LoadingContent,
  LeafLeftContent,
} from './styles';

interface IProject {
  type: string;
  status: 'Pendente' | 'Finalizado' | 'Cancelado';
  approval_min_price?: number | null;
  approval_max_price?: number | null;
  project_procedure: {
    id: string;
    sequence: number;
  };
}

interface INewProjectData {
  type: string;
  erp_client_code?: string;
  erp_client_name?: string;
  erp_client_company_name?: string;
  erp_client_document?: string;
  erp_client_address?: string;
  erp_client_city?: string;
  erp_client_state?: string;
  erp_rep_code: string;
  rep_as_client: boolean;
}

interface ILocation {
  type: string;
  project_id?: string;
  project_type?: string;
  erp_client_code?: string;
}

const ExternalAccess: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();
  const { state } = useLocation<ILocation>();

  useEffect(() => {
    const getExistingProject = async (projectId: string) => {
      let project: IProject | undefined;

      await api.get(`/projects/id?id=${projectId}`).then(response => {
        project = response.data;
      });

      if (project) {
        if (
          getEditProjPerm({
            status: project.status,
            ft_proj_proc_id: project.project_procedure.id,
            user,
            approval_min_price: project.approval_min_price,
            approval_max_price: project.approval_max_price,
          })
        ) {
          history.push({
            pathname: `${
              PROJECT_PATHS[project.type as keyof typeof PROJECT_PATHS].path
            }/procedure${project.project_procedure.sequence}`,
            state: project,
          });
        } else {
          addToast({
            type: 'error',
            title: 'Operação inválida!',
            description: 'Usuário sem permissão',
          });
        }
      } else {
        addToast({
          type: 'error',
          title: 'Operação inválida!',
          description: 'Solicitação não encontrada',
        });

        history.push('/');
      }
    };

    const getNewProject = async (
      erpClientCode: string,
      projectType: string,
    ) => {
      let newProjData: INewProjectData | undefined;

      await api.get(`/clients/code?code=${erpClientCode}`).then(response => {
        newProjData = {
          type: projectType,
          erp_client_code: response.data.code,
          erp_client_name: response.data.name,
          erp_client_company_name: response.data.company_name,
          erp_client_document: response.data.document,
          erp_client_address: response.data.address,
          erp_client_city: response.data.city,
          erp_client_state: response.data.state,
          erp_rep_code: response.data.rep_code,
          rep_as_client: false,
        };
      });

      if (newProjData) {
        if (
          user.user_type.type === 'REP' &&
          user.erp_rep_code !== newProjData.erp_rep_code
        ) {
          addToast({
            type: 'error',
            title: 'Operação inválida!',
            description: 'Usuário sem permissão',
          });

          history.push('/');
        } else {
          history.push({
            pathname: `${
              PROJECT_PATHS[projectType as keyof typeof PROJECT_PATHS].path
            }/procedure1/new`,
            state: newProjData,
          });
        }
      } else {
        addToast({
          type: 'error',
          title: 'Operação inválida!',
          description: 'Cliente não localizado',
        });

        history.push('/');
      }
    };

    if (
      (state.type === 'existing_project' || state.type === 'new_project') &&
      !user
    ) {
      history.push({
        pathname: '/',
        state,
      });
    } else if (state.type === 'existing_project' && state.project_id && user) {
      getExistingProject(state.project_id);
    } else if (
      state.type === 'new_project' &&
      state.project_type &&
      state.erp_client_code &&
      user
    ) {
      getNewProject(state.erp_client_code, state.project_type);
    } else {
      history.push('/');
    }
  }, [addToast, history, state, user]);

  return (
    <Container>
      <Content>
        <LeafRightContent>
          <img src={leafRightImg} alt="Right leaf" />
        </LeafRightContent>

        <LogoContent>
          <img src={logoImg} alt="Guararapes Logo" />
        </LogoContent>

        <InfoContent>
          <div>Validando Solicitação</div>
        </InfoContent>

        <LoadingContent>
          <FadeLoader />
        </LoadingContent>

        <LeafLeftContent>
          <img src={leafLeftImg} alt="Left leaf" />
        </LeafLeftContent>
      </Content>
    </Container>
  );
};

export default ExternalAccess;
